import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star, Filter, Lock, ArrowRight, ArrowLeft } from 'lucide-react';
import { toStringArray } from '@/lib/utils';
import { CoachImage } from '@/components/CoachImage';

const API_URL = window.location.origin;

const CoachesPage = () => {
  const { user, hasTier } = useAuth();
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState([]);
  const [filteredCoaches, setFilteredCoaches] = useState([]);
  const [matchedCoach, setMatchedCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [specialty, setSpecialty] = useState('all');

  // Check if user is Inner Edge (can only see assigned coach)
  const isInnerEdgeOnly = user && hasTier('inner_edge') && !hasTier('sdc') && !hasTier('coaching_activation');

  const specialties = [
    'All Specialties',
    'Executive Leadership',
    'Business Growth',
    'Emotional Intelligence',
    'Team Leadership',
    'Change Management',
    'Diversity & Inclusion',
    'Neuroscience',
    'Strategic Thinking'
  ];

  useEffect(() => {
    if (isInnerEdgeOnly) {
      fetchMatchedCoach();
    } else {
      fetchCoaches();
    }
  }, [isInnerEdgeOnly]);

  useEffect(() => {
    filterCoaches();
  }, [specialty, coaches]);

  const fetchCoaches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/coaches`);
      setCoaches(response.data);
      setFilteredCoaches(response.data);
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchedCoach = async () => {
    try {
      // Get the user's matched coach from their diagnostic result
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/diagnostic/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // The API returns matched coach at coach_match.coach
      const coachData = response.data?.coach_match?.coach;
      if (coachData) {
        setMatchedCoach(coachData);
        setCoaches([coachData]);
        setFilteredCoaches([coachData]);
      }
    } catch (error) {
      console.error('Failed to fetch matched coach:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterCoaches = () => {
    let result = [...coaches];
    
    if (specialty && specialty !== 'all') {
      result = result.filter(coach =>
        (coach.specialties || []).some(s => String(s).toLowerCase().includes(specialty.toLowerCase()))
      );
    }
    
    setFilteredCoaches(result);
  };

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      
      {/* Back to Home Link */}
      <div className="bg-forest pt-20 pb-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">Back to Home</span>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-forest pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              {isInnerEdgeOnly ? 'Your Assigned Guide' : 'Our Seasoned and Certified Coaches'}
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              {isInnerEdgeOnly 
                ? 'Based on your diagnostic results, you have been matched with the ideal guide for your transformation journey.'
                : 'Diverse, vetted coaches bringing the experiences and expertise that matter for your unique journey.'}
            </p>
          </div>
        </div>
      </section>

      {/* Ubuntu Philosophy Banner */}
      {!isInnerEdgeOnly && (
        <section className="py-8 bg-sand/50">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-forest font-medium mb-2">
              <em>"I am because we are"</em> — Ubuntu Philosophy
            </p>
            <p className="text-gray-600 text-sm max-w-2xl mx-auto">
              We believe in wholeness and that we can all learn from one another. Our diverse coaches are personally vetted by 
              Crystal &amp; Dr. Nicole for their credentials, experience, and alignment with our values. 
              Your breakthrough might come from a voice you didn't expect.
            </p>
          </div>
        </section>
      )}

      {/* Inner Edge Restriction Notice */}
      {isInnerEdgeOnly && !matchedCoach && (
        <section className="py-8">
          <div className="max-w-3xl mx-auto px-4">
            <Card className="card-base border-2 border-terracotta/30">
              <CardContent className="p-8 text-center">
                <Lock className="h-12 w-12 text-terracotta mx-auto mb-4" />
                <h2 className="font-heading text-2xl text-forest mb-3">Complete Your Diagnostic First</h2>
                <p className="text-gray-600 mb-6">
                  As an Inner Edge member, you'll be matched with an ideal coach based on your unique leadership profile. 
                  Complete the diagnostic to get your personalized coach match.
                </p>
                <Link to="/diagnostic">
                  <Button className="btn-primary">
                    Start Diagnostic <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Filters - Only show for non-Inner Edge users */}
      {!isInnerEdgeOnly && (
        <section className="py-8 bg-white border-b border-sand sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row gap-4 md:justify-end">
            <Select value={specialty} onValueChange={setSpecialty}>
              <SelectTrigger className="w-full md:w-64 input-base" data-testid="specialty-filter">
                <Filter className="h-4 w-4 mr-2 text-gray-400" />
                <SelectValue placeholder="Filter by specialty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Specialties</SelectItem>
                {specialties.slice(1).map((spec) => (
                  <SelectItem key={spec} value={spec.toLowerCase()}>
                    {spec}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>
      )}

      {/* Coaches Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!isInnerEdgeOnly && (
            <div className="mb-6">
              <p className="text-gray-600">
                Showing {filteredCoaches.length} of {coaches.length} coaches
              </p>
            </div>
          )}

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="card-base animate-pulse">
                  <div className="h-48 bg-sand rounded-lg mb-4" />
                  <div className="h-6 bg-sand rounded w-3/4 mb-2" />
                  <div className="h-4 bg-sand rounded w-1/2 mb-4" />
                  <div className="h-16 bg-sand rounded" />
                </div>
              ))}
            </div>
          ) : filteredCoaches.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No coaches found matching your criteria.</p>
              <Button 
                variant="outline" 
                className="mt-4 btn-outline"
                onClick={() => setSpecialty('all')}
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCoaches.map((coach) => (
                <Link to={`/coaches/${coach.id}`} key={coach.id}>
                  <Card className="card-base coach-card h-full" data-testid={`coach-card-${coach.id}`}>
                    <CardContent className="p-0">
                      <div className="relative mb-4">
                        <CoachImage
                          src={coach.image_url}
                          alt={coach.name}
                          name={coach.name}
                          className="w-full h-56 object-cover rounded-lg"
                        />
                        <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-sm">
                          <Star className="h-4 w-4 text-terracotta fill-terracotta" />
                          <span className="font-semibold text-forest">{coach.average_rating}</span>
                          <span className="text-gray-500 text-sm">({coach.total_reviews})</span>
                        </div>
                      </div>
                      
                      <h3 className="font-heading text-xl font-semibold text-forest">{coach.name}</h3>
                      <p className="text-terracotta text-sm font-medium mb-3">{coach.title}</p>
                      
                      <p className="text-gray-600 text-sm line-clamp-3 mb-4">{coach.short_bio}</p>
                      
                      {(() => {
                        const creds = toStringArray(coach.credentials);
                        if (creds.length === 0) return null;
                        return (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {creds.slice(0, 3).map((cred, i) => (
                              <span key={i} className="px-2 py-1 bg-forest/10 text-forest text-xs rounded font-medium">
                                {cred}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                      
                      <div className="flex flex-wrap gap-2">
                        {toStringArray(coach.specialties).slice(0, 3).map((specialty, i) => (
                          <span key={i} className="px-3 py-1 bg-sand text-forest text-xs rounded-full">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CoachesPage;
